import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { useEffect } from 'react'
import API from '../services'

/**
 * Last app opens per member + billing period for the Members page stats.
 * Only the owner and members with canEditMembers can load it.
 */
const useGymEngagement = (gymID: string | null, canView: boolean) => {
  const query = useQuery({
    queryKey: ['gymEngagement', gymID],
    queryFn: () => API.getGymEngagement(gymID as string),
    enabled: !!gymID && canView,
    // The server queries PostHog on every call, so don't refetch on every
    // tab switch
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

  return {
    engagement: query.data,
    engagementIsLoading: query.isLoading && canView,
  }
}

/** Active members per month for the last six months (Members page chart) */
export const useGymMonthlyActivity = (gymID: string | null, canView: boolean) =>
  useQuery({
    queryKey: ['gymMonthlyActivity', gymID],
    queryFn: () => API.getGymMonthlyActivity(gymID as string),
    enabled: !!gymID && canView,
    // Completed months only, so this changes once a month
    staleTime: 60 * 60 * 1000,
    retry: 1,
  })

export type ViewWindow = {
  /** Calendar month title, e.g. "August 2026" */
  title: string
  from: number
  to: number
}

export const VIEW_HISTORY_MONTHS = 12

/**
 * Calendar months going back from `historyEnd` (day-aligned, e.g. the start
 * of "last 30 days"). The first month is cut off at `historyEnd`, so windows
 * never overlap the period before it. Day-aligned bounds also keep requests
 * identical through the day, so PostHog can answer them from its cache.
 */
export const getViewWindow = (historyEnd: number, index: number) => {
  const month = DateTime.fromMillis(historyEnd - 1)
    .startOf('month')
    .minus({ months: index })
  const window: ViewWindow = {
    title: month.toFormat('LLLL yyyy'),
    from: month.toMillis(),
    to: index === 0 ? historyEnd : month.plus({ months: 1 }).toMillis(),
  }
  return window
}

/**
 * Members' last views one month at a time, newest first, for infinite
 * scroll lists. Each page is a small PostHog query (~1s), where a whole year
 * at once takes ~6s. Starts only when `isEnabled`, and pages are fetched one
 * after another, which stays within PostHog's limit of 3 concurrent queries
 * per project. Covers current and blocked members alike.
 */
export const useMemberViewHistory = (
  gymID: string | null,
  historyEnd: number,
  isEnabled: boolean
) =>
  useInfiniteQuery({
    queryKey: ['memberViewHistory', gymID, historyEnd],
    queryFn: async ({ pageParam }) => {
      const window = getViewWindow(historyEnd, pageParam)
      const { lastViewedAt } = await API.getMemberLastViews(
        gymID as string,
        window.from,
        window.to
      )
      return { window, lastViewedAt }
    },
    initialPageParam: 0,
    getNextPageParam: (_lastPage, pages) =>
      pages.length < VIEW_HISTORY_MONTHS ? pages.length : undefined,
    enabled: !!gymID && isEnabled,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

/**
 * Every member's latest view over the past year, loading all months one
 * after another, for lists sorted by tracked or joined date (where any row
 * can be anywhere). Stops early once every member in `uids` has a view.
 * Shares its month cache with ActivityHistoryList lists that end today.
 */
export const useAllMemberViews = (
  gymID: string | null,
  uids: string[],
  isEnabled: boolean
) => {
  const historyEnd = DateTime.now().startOf('day').plus({ days: 1 }).toMillis()
  const history = useMemberViewHistory(gymID, historyEnd, isEnabled)

  // Pages go newest to oldest, so keep the first (latest) view per member
  const views: { [uid: string]: number } = {}
  history.data?.pages.forEach(({ lastViewedAt }) =>
    Object.entries(lastViewedAt || {}).forEach(([uid, at]) => {
      if (!(uid in views)) views[uid] = at
    })
  )

  const allFound = uids.every((uid) => uid in views)
  const { fetchNextPage, isFetching, hasNextPage, isError } = history
  const hasData = !!history.data
  const isComplete =
    isEnabled && (allFound || (hasData && !hasNextPage) || isError)

  useEffect(() => {
    if (
      isEnabled &&
      hasData &&
      hasNextPage &&
      !isFetching &&
      !allFound &&
      !isError
    )
      fetchNextPage()
  }, [
    isEnabled,
    hasData,
    hasNextPage,
    isFetching,
    allFound,
    isError,
    fetchNextPage,
  ])

  return { views, isComplete }
}

export default useGymEngagement
